import React, { useState, useRef, useEffect, useMemo } from "react";
import { SearchInput } from "components/input";
import axios from "axios";
import { VendorItemDetails } from "types/vendor";
import { toast } from "react-toastify";
import { useQuery, useQueryClient } from "react-query";

import { getInventoryCategories, getInventoryItems, getSiteoneInventoryCategories, getSiteoneInventoryItems, saveSiteoneLinkedItem, changeSiteoneItemLink, unlinkSiteoneItemLink } from "api/inventoryApi";
import { TInventory, TInventoryCategory } from "../../../../../types/inventory";
import Spinner from "components/spinner";

import styles from "./VendorItemLinkModalBody.module.scss";
import { Button } from "components/button";
import { Input } from "components/input";
import { useTranslation } from 'react-i18next';
import { useSnackbarContext } from "components/snackbar";
import LinkIcon from '@material-ui/icons/Link';
import { createPromiseCapability } from "pdfjs-dist";
import NoProductImage from "../../../../../images/placeholders/no-product.jpg";
import VendorLeadTimeInput from "views/vendors/components/VendorLeadTimeInput";
import VendorItemNotesInput from "views/vendors/components/VendorItemNotesInput";
import VendorItemPriceInput from "views/vendors/components/VendorItemPriceInput";
import { formatCentsToVendorPriceInput } from "views/vendors/constants/vendorItemPrice";
import { clampVendorItemNotes } from "views/vendors/constants/vendorItemFields";
import { normalizeLeadTimeStored } from "views/vendors/constants/vendorLeadTime";


interface Props {
	vendorId: number | undefined;
}

function getGreenShade(score: number, maxScore: number): string {
	// Range from light to dark green
	const percent = score / maxScore;
	const light = 240; // lightest green value
	const dark = 80;   // darkest green value

	const greenValue = Math.floor(light - (light - dark) * percent);
	return `rgb(${greenValue}, 255, ${greenValue})`;
}

function findTopMatches(item: TInventory, siteoneItems: TInventory[]): { item: TInventory; score: number }[] {
	if (!item || !siteoneItems?.length) return [];

	return siteoneItems
		.map(candidate => ({
			item: candidate,
			score: matchScore(item, candidate),
		}))
		.filter(entry => entry.score >= 1)
		.sort((a, b) => b.score - a.score);
}

function normalizeAndTokenize(str: string): string[] {
	return str
		.toLowerCase()
		.replace(/[^a-z0-9\s]/gi, "")  // keep spaces to preserve word boundaries
		.split(/\s+/)                 // split on whitespace
		.filter(Boolean);            // remove empty strings
}

function matchScore(item: TInventory, candidate: TInventory): number {
	const itemTokens = normalizeAndTokenize(item.item_name || item.description || "");
	const candidateTokens = normalizeAndTokenize(candidate.item_name || candidate.description || "");

	// Count overlapping words
	let score = 0;
	for (const word of itemTokens) {
		if (candidateTokens.includes(word)) score++;
	}

	return score;
}

function formatListPriceForInput(listPrice: number | null | undefined): string {
	return formatCentsToVendorPriceInput(listPrice);
}

function getItemDisplayName(item: any): string {
	// Some inventory rows may have `item_name` missing; fall back to other text fields
	// so the list doesn't appear empty on initial load.
	const longDesc =
		typeof item?.long_description === "string" ? item.long_description.slice(0, 80) : "";
	return (
		item?.vendor_part_number ||
		item?.item_name ||
		item?.short_description ||
		longDesc ||
		item?.model ||
		item?.sku ||
		(item?.item_id != null ? `Item ${item.item_id}` : null) ||
		"Untitled"
	);
}



const VendorItemLinkModalBody = ({ vendorId, isSiteoneVendor, isHeritageVendor }: any) => {
	const { t } = useTranslation();
	const tBase = "views.vendors.VendorBody.VendorItems.VendorItemLinkModal.VendorItemLinkModalBody";
	const isIntegratedVendor = Boolean(isSiteoneVendor || isHeritageVendor);
	const tr = (key: string) => t(`${tBase}.${key}`);
	const trAction = (linkKey: string, addKey: string) =>
		tr(isIntegratedVendor ? linkKey : addKey);

	const { showSnackbar } = useSnackbarContext();
	const queryClient = useQueryClient();

	const [selectedCategory, setSelectedCategory] = useState<TInventoryCategory | null>(null);
	const [siteoneSelectedCategory, setSiteoneSelectedCategory] = useState<TInventoryCategory | null>(null);
	const [selectedItem, setSelectedItem] = useState<TInventory | null>(null);
	const [selectedSiteoneItem, setSelectedSiteoneItem] = useState<TInventory | null>(null);
	const [price, setPrice] = useState<string>("");
	const [leadTime, setLeadTime] = useState<string>("");
	const [notes, setNotes] = useState<string>("");
	const [recommendedSiteoneItems, setRecommendedSiteoneItems] = useState<{ item: TInventory; score: number }[]>([]);
	const [recommendedSiteoneCategory, setRecommendedSiteoneCategory] = useState<TInventoryCategory | null>(null);
	const [isSearchingLinkedSiteoneItem, setIsSearchingLinkedSiteoneItem] = useState(false);
	const [isSearchingLinkedLocalItem, setIsSearchingLinkedLocalItem] = useState(false);

	const recommendedItemRef = useRef<HTMLDivElement | null>(null);
	const [recommendedIndex, setRecommendedIndex] = useState(0);
	const currentRecommendedItem = recommendedSiteoneItems[recommendedIndex]?.item || null;

	const [linkedItems, setLinkedItems] = useState<any>(
		{
			linkedItems: [],
			linkedSiteoneItems: []
		}
	);

	const [unlinkedItems, setUnlinkedItems] = useState<any>(
		{
			unlinkedItems: [],
			unlinkedSiteoneItems: []
		}
	);

	const [userItems, setUserItems] = useState<VendorItemDetails[]>([]);
	const [search, setSearch] = useState("");
	const [heritageResults, setHeritageResults] = useState<any[]>([]);
	const [localSearch, setLocalSearch] = useState("");

	const [siteoneSiteoneItemSearch, setSiteoneSiteoneItemSearch] = useState("");
	const [siteoneSiteoneItemNumberSearch, setSiteoneSiteoneItemNumberSearch] = useState("");
	const [siteoneLocalItemSearch, setSiteoneLocalItemSearch] = useState("");
	const [siteoneLocalItemNumberSearch, setSiteoneLocalItemNumberSearch] = useState("");
	const [siteoneItemNumberSearch, setSiteoneItemNumberSearch] = useState(false);
	const [localItemNumberSearch, setLocalItemNumberSearch] = useState(false);
	const [linkItemSearch, setLinkItemSearch] = useState("");


	useEffect(() => {
		if (!vendorId) return;
		const endpoint = `${process.env.REACT_APP_SERVER_URL}/api/v1/company/heritage/local-items/search`;
		const params = localSearch.trim().length >= 2 ? { q: localSearch } : {};
		axios.get(endpoint, { params }).then((res) => setUserItems(res.data.items || []));
	}, [vendorId, localSearch]);

	useEffect(() => {
		const endpoint = `${process.env.REACT_APP_SERVER_URL}/api/v1/company/heritage/heritage-items/search`;
		const params = search.trim().length >= 2 ? { q: search } : {};
		axios.get(endpoint, { params }).then((res) => setHeritageResults(res.data.items || []));
	}, [search]);

	const handleLink = async (heritagePartNumber: string) => {
		if (!selectedItem || !vendorId) return;

		const localItem = userItems.find((i: any) => i.item_id === selectedItem);
		const confirm = window.confirm(
			`Are you sure you want to link "${localItem?.item_name}" to "${heritagePartNumber}"?`
		);
		if (!confirm) return;

		try {
			await axios.post(`${process.env.REACT_APP_SERVER_URL}/api/v1/company/heritage/link`, {
				itemId: selectedItem,
				vendorId,
				heritagePartNumber,
			});
			toast.success(`Linked "${localItem?.item_name}" to "${heritagePartNumber}"`, { autoClose: 2500 });

			// Re-fetch local items to update link state
			const endpoint = `${process.env.REACT_APP_SERVER_URL}/api/v1/company/heritage/local-items/search`;
			const params = localSearch.trim().length >= 2 ? { q: localSearch } : {};
			axios.get(endpoint, { params }).then((res) => setUserItems(res.data.items || []));

		} catch {
			toast.error("Failed to link item.");
		}
	};

	const handleUnlink = async () => {
		if (!selectedItem || !vendorId) return;

		const localItem = userItems.find((i: any) => i.item_id === selectedItem);
		const confirm = window.confirm(`Unlink "${localItem?.item_name}" from vendor item?`);
		if (!confirm) return;

		try {
			await axios.post(`${process.env.REACT_APP_SERVER_URL}/api/v1/company/heritage/unlink`, {
				itemId: selectedItem,
				vendorId,
			});
			toast.success(`Unlinked "${localItem?.item_name}"`, { autoClose: 2500 });

			// Re-fetch local items only
			const endpoint = `${process.env.REACT_APP_SERVER_URL}/api/v1/company/heritage/local-items/search`;
			const params = localSearch.trim().length >= 2 ? { q: localSearch } : {};
			axios.get(endpoint, { params }).then((res) => setUserItems(res.data.items || []));

		} catch {
			toast.error("Failed to unlink item.");
		}
	};

	const categoryQuery = useQuery(["categories"], getInventoryCategories);

	// Search query — used when a search string is entered
	const allItemsBySearch = useQuery(
		["vendor_link_items_search", siteoneLocalItemSearch, siteoneLocalItemNumberSearch],
		() => getInventoryItems(null, siteoneLocalItemSearch, siteoneLocalItemNumberSearch),
		{
			enabled: !!siteoneLocalItemSearch?.trim() || !!siteoneLocalItemNumberSearch?.trim(), // only when search is active
		}
	);

	// Category-only query — used when there's no search
	const categoryItemsQuery = useQuery(
		["vendor_link_items_by_category", selectedCategory?.category_id],
		() => getInventoryItems(selectedCategory?.category_id || null, null, null),
		{
			enabled: !siteoneLocalItemSearch?.trim() && !siteoneLocalItemNumberSearch?.trim() && !!selectedCategory,
		}
	);

	const activeItems = siteoneLocalItemSearch?.trim() || siteoneLocalItemNumberSearch?.trim()
		? allItemsBySearch.data || []
		: categoryItemsQuery.data || [];


	const filteredCategories = useMemo(() => {
		const categories = categoryQuery.data || [];

		// If no search, return all
		if (!allItemsBySearch.data || (!siteoneLocalItemSearch?.trim() && !siteoneLocalItemNumberSearch?.trim())) {
			return categories;
		}

		const matchedCategoryIds = new Set(
			allItemsBySearch.data.flatMap((item: TInventory) => [
				item.root_id,
				item.cat_id_v2,
				item.category_id,
			])
		);

		return categories.filter((cat: any) =>
			matchedCategoryIds.has(cat.category_id) ||
			matchedCategoryIds.has(cat.root_id) ||
			matchedCategoryIds.has(cat.cat_id_v2)
		);
	}, [categoryQuery.data, allItemsBySearch.data, siteoneLocalItemSearch, siteoneLocalItemNumberSearch]);



	const filteredItems = useMemo(() => {
		if (!selectedCategory || !activeItems) return [];

		return activeItems.filter((item: TInventory) => {
			const catId = selectedCategory.category_id;
			return (
				item.root_id === catId ||
				item.cat_id_v2 === catId ||
				item.category_id === catId
			);
		})
	}, [activeItems, selectedCategory, selectedItem]);

	// Default vendor link flow — search filters categories and items together
	const linkItemsBySearch = useQuery(
		["vendor_link_items_search_default", linkItemSearch],
		() => getInventoryItems(null, linkItemSearch, null),
		{
			enabled: !!linkItemSearch?.trim(),
		}
	);

	const linkCategoryItemsQuery = useQuery(
		["vendor_link_items_by_category_default", selectedCategory?.category_id],
		() => getInventoryItems(selectedCategory?.category_id || null, null, null),
		{
			enabled: !linkItemSearch?.trim() && !!selectedCategory,
		}
	);

	const linkActiveItems = linkItemSearch?.trim()
		? linkItemsBySearch.data || []
		: linkCategoryItemsQuery.data || [];

	const linkFilteredCategories = useMemo(() => {
		const categories = categoryQuery.data || [];

		if (!linkItemsBySearch.data || !linkItemSearch?.trim()) {
			return categories;
		}

		const matchedCategoryIds = new Set(
			linkItemsBySearch.data.flatMap((item: TInventory) => [
				item.root_id,
				item.cat_id_v2,
				item.category_id,
			])
		);

		return categories.filter((cat: any) =>
			matchedCategoryIds.has(cat.category_id) ||
			matchedCategoryIds.has(cat.root_id) ||
			matchedCategoryIds.has(cat.cat_id_v2)
		);
	}, [categoryQuery.data, linkItemsBySearch.data, linkItemSearch]);

	const linkFilteredItems = useMemo(() => {
		if (!selectedCategory || !linkActiveItems) return [];

		return linkActiveItems.filter((item: TInventory) => {
			const catId = selectedCategory.category_id;
			return (
				item.root_id === catId ||
				item.cat_id_v2 === catId ||
				item.category_id === catId
			);
		});
	}, [linkActiveItems, selectedCategory]);

	useEffect(() => {
		if (!linkItemSearch?.trim() || linkFilteredCategories.length === 0) return;

		setSelectedCategory((prev) => {
			if (prev && linkFilteredCategories.some((c) => c.category_id === prev.category_id)) {
				return prev;
			}
			return linkFilteredCategories[0];
		});
	}, [linkFilteredCategories, linkItemSearch]);

	useEffect(() => {
		if (!linkItemSearch?.trim() || !selectedItem) return;

		const stillVisible = linkFilteredItems.some(
			(i) => String(i.item_id) === String(selectedItem.item_id)
		);
		if (!stillVisible) {
			setSelectedItem(null);
			setPrice("");
		}
	}, [linkFilteredItems, linkItemSearch, selectedItem]);

	useEffect(() => {
		if (activeItems.length > 0 && filteredCategories.length > 0) {
			setSelectedCategory(filteredCategories[0]);
		}
	}, [filteredCategories]);


	const siteoneCategoryQuery = useQuery(["siteone_categories"], getSiteoneInventoryCategories);
	const allSiteoneItemsQuery = useQuery(
		["siteone_vendor_link_items_all", siteoneSiteoneItemSearch, siteoneSiteoneItemNumberSearch],
		async () => {
			// null categoryId so it doesn't filter by category server-side
			return getSiteoneInventoryItems(null, siteoneSiteoneItemSearch, siteoneSiteoneItemNumberSearch);
		},
		{
			enabled: !!siteoneSiteoneItemSearch?.trim() || !!siteoneSiteoneItemNumberSearch?.trim(), // only when search is active
		}
	);

	const siteoneCategoryItemsQuery = useQuery(
		["siteone_vendor_link_items_category", siteoneSelectedCategory?.category_id],
		async () => {
			return getSiteoneInventoryItems(siteoneSelectedCategory?.category_id ?? null, null, null);
		},
		{
			enabled: !siteoneSiteoneItemSearch?.trim() || !siteoneSiteoneItemNumberSearch?.trim() || !!siteoneSelectedCategory,
		}
	);


	// if no string search is active, get all siteone items
	const activeSiteoneItems = siteoneSiteoneItemSearch?.trim() || siteoneSiteoneItemNumberSearch?.trim()
		? allSiteoneItemsQuery.data || []
		: siteoneCategoryItemsQuery.data || [];


	const filteredSiteoneCategories = useMemo(() => {
		if (siteoneCategoryQuery.isLoading || !siteoneCategoryQuery.data) return [];

		const categories = siteoneCategoryQuery.data.filter((cat: any) => cat.category_id != null);

		if (!allSiteoneItemsQuery.data || (!siteoneSiteoneItemSearch?.trim() && !siteoneSiteoneItemNumberSearch?.trim())) {
			return categories;
		}

		const matchedCategoryIds = new Set(
			allSiteoneItemsQuery.data.map((item: TInventory) => item.line)
		);
		// console.log("matchedCategoryIdssiteone", matchedCategoryIds);

		return categories.filter((cat: any) =>
			matchedCategoryIds.has(cat.category_id)
		);
	}, [siteoneCategoryQuery.data, allSiteoneItemsQuery.data, siteoneSiteoneItemSearch, siteoneSiteoneItemNumberSearch]);


	const filteredSiteoneItems = useMemo(() => {
		if (!activeSiteoneItems || !siteoneSelectedCategory) return [];

		return activeSiteoneItems.filter((item: TInventory) =>
			item.line === siteoneSelectedCategory.category_id
		);
	}, [activeSiteoneItems, siteoneSelectedCategory]);






	useEffect(() => {
		if (activeSiteoneItems.length > 0 && filteredSiteoneCategories.length > 0) {
			setSiteoneSelectedCategory(filteredSiteoneCategories[0]);
		}
	}, [filteredSiteoneCategories]);





	useEffect(() => {
		if (recommendedSiteoneItems.length > 0) {
			setTimeout(() => {
				recommendedItemRef.current?.scrollIntoView({
					behavior: "smooth",
					block: "center"
				});
			}, 0);
		}
	}, [recommendedIndex, recommendedSiteoneItems]);

	let siteoneItemLoading = false;

	useEffect(() => {
		siteoneItemLoading = false
	}, [selectedSiteoneItem]);

	// match loading is used when toggling the link siteone item button, to prevent it from being toggled multiple times between item clicks
	let matchLoading = false;

	useEffect(() => {
		matchLoading = true;
		setIsSearchingLinkedSiteoneItem(true);
		const fetchLinkedSiteoneItem = async () => {
			if (!selectedItem || !siteoneCategoryQuery.data) {
				setIsSearchingLinkedSiteoneItem(false);
				return;
			}

			for (const category of siteoneCategoryQuery.data) {
				const items = await getSiteoneInventoryItems(category.category_id);
				if (!items || items.length === 0) continue;
				let match = null
				for (const item of items) {
					if (item.linked_inventory_item_id !== null) {
						// if (!item.linked_inventory_item_id) continue; // Skip if not linked
						if (parseInt(item.linked_inventory_item_id) === parseInt(selectedItem.item_id)) {
							match = item;
							break;
						}

					}
				}
				// const match = items?.find(
				// 	(sItem: TInventory) => sItem.linked_inventory_item_id === selectedItem.item_id
				// );
				if (match) {
					setSelectedSiteoneItem(match)
					setSiteoneSelectedCategory({ category_id: match.line, category_name: match.line });

					break;
				}

			}
			matchLoading = false;
			setIsSearchingLinkedSiteoneItem(false);
		};

		fetchLinkedSiteoneItem();
	}, [selectedItem, siteoneCategoryQuery.data]);



	const handleLinkSiteoneItemToInventoryItem = async () => {
		if (!selectedItem || !selectedSiteoneItem) return;

		try {
			const res = await saveSiteoneLinkedItem(selectedItem.item_id, selectedSiteoneItem.id);

			updateLinkedState(res);
			showSnackbar("SiteOne Item Linked Successfully");
		} catch {
			showSnackbar("Error Linking SiteOne Item");
		}
	};

	const handleChangeLinkSiteoneItemToInventoryItem = async () => {
		if (!selectedItem || !selectedSiteoneItem) return;

		try {
			const res = await changeSiteoneItemLink(selectedItem.item_id, selectedSiteoneItem.id);
			// Reset existing link state first (same as unlink)
			clearLinkState(res.fk_inventory_item_id, res.fk_siteone_inventory_item_id);
			updateLinkedState(res);
			showSnackbar("SiteOne Item Linked Successfully");
		} catch {
			showSnackbar("Error Linking SiteOne Item");
		}
	};

	const handleUnlinkSiteoneItemToInventoryItem = async () => {
		if (!selectedItem || !selectedSiteoneItem) return;

		try {
			await unlinkSiteoneItemLink(selectedItem.item_id, selectedSiteoneItem.id);
			clearLinkState(selectedItem.item_id, selectedSiteoneItem.id);
			showSnackbar("SiteOne Item Unlinked Successfully");
		} catch {
			showSnackbar("Error Unlinking SiteOne Item");
		}
	};


	const handleChangeCategory = (category: TInventoryCategory) => {
		setSelectedCategory(category);
		setSelectedItem(null);
		setSiteoneSelectedCategory(null);
		setSelectedSiteoneItem(null);
		setPrice("");
		setLeadTime("");
		setNotes("");
		// Clear search fields
		setSiteoneSiteoneItemSearch("");
		setSiteoneSiteoneItemNumberSearch("");
	};

	const handleSiteoneChangeCategory = (category: TInventoryCategory) => {
		setSiteoneSelectedCategory(category);
		setSelectedSiteoneItem(null);
	};

	const selectInventoryItem = (item: TInventory) => {
		setSelectedItem(item);
		setPrice(formatListPriceForInput(item.list_price));
	};

	const handleChangeItem = (item: TInventory) => {
		selectInventoryItem(item);

		// If item is already linked, clear search and let useEffect find the linked SiteOne item
		if (item.linked_inventory_item_id) {
			setSiteoneSiteoneItemSearch("");
			setSiteoneSiteoneItemNumberSearch("");
		} else {
			// Only auto-populate search if item is NOT linked
			if (siteoneItemNumberSearch) {
				setSiteoneSiteoneItemNumberSearch(item.model);
			} else {
				setSiteoneSiteoneItemSearch(item.item_name);
			}
		}
	};

	const handleSiteoneChangeItem = (item: TInventory) => {
		setSelectedSiteoneItem(item);
		
		// If the SiteOne item is linked to a local item, find and select that local item
		if (item.linked_inventory_item_id) {
			findLinkedLocalItem(item.linked_inventory_item_id);
		}
	};

	const findLinkedLocalItem = async (linkedInventoryItemId: string | number) => {
		setIsSearchingLinkedLocalItem(true);
		try {
			// First, try to find the item in already cached data
			const cachedItems = allItemsBySearch.data || [];
			const linkedLocalItem = cachedItems.find(
				(localItem: TInventory) => parseInt(localItem.item_id) === parseInt(String(linkedInventoryItemId))
			);

			if (linkedLocalItem) {
				// Find the category for this item
				const categories = categoryQuery.data || [];
				const itemCategory = categories.find(
					(cat: any) => cat.category_id === linkedLocalItem.category_id
				);
				
				if (itemCategory) {
					setSelectedCategory(itemCategory);
					selectInventoryItem(linkedLocalItem);
					setIsSearchingLinkedLocalItem(false);
					return;
				}
			}

			// If not found in cached data, make a single API call to get all items
			const allItems = await getInventoryItems(null, null, null);
			if (!allItems || allItems.length === 0) {
				setIsSearchingLinkedLocalItem(false);
				return;
			}

			// Find the local item that matches the linked_inventory_item_id
			const foundItem = allItems.find(
				(localItem: TInventory) => parseInt(localItem.item_id) === parseInt(String(linkedInventoryItemId))
			);

			if (foundItem) {
				// Find the category for this item
				const categories = await getInventoryCategories();
				const itemCategory = categories.find(
					(cat: any) => cat.category_id === foundItem.category_id
				);
				
				if (itemCategory) {
					setSelectedCategory(itemCategory);
					selectInventoryItem(foundItem);
				}
			}
		} catch (error) {
			console.error("Error finding linked local item:", error);
		} finally {
			setIsSearchingLinkedLocalItem(false);
		}
	};


	const updateLinkedState = (res: any) => {
		// update the item in the filteredItems list to have the linked_inventory_item_id
		const updatedItem = filteredItems.find((i: TInventory) => parseInt(i.item_id) === parseInt(res.fk_inventory_item_id));
		if (updatedItem) {
			updatedItem.linked_inventory_item_id = res.fk_siteone_inventory_item_id;
		}

		// update the item in the filteredSiteoneItems list to have the linked_inventory_item_id
		const updatedSiteoneItem = filteredSiteoneItems.find((i: TInventory) => parseInt(i.id as any) === parseInt(res.fk_siteone_inventory_item_id));
		if (updatedSiteoneItem) {
			updatedSiteoneItem.linked_inventory_item_id = res.fk_inventory_item_id;
		}

		setLinkedItems((prev: any) => ({
			linkedItems: [...prev.linkedItems, res.fk_inventory_item_id],
			linkedSiteoneItems: [...prev.linkedSiteoneItems, res.fk_siteone_inventory_item_id]
		}));
		setSelectedItem((prev: any) => ({
			...prev,
			linked_inventory_item_id: res.fk_siteone_inventory_item_id
		}));
		setSelectedSiteoneItem((prev: any) => ({
			...prev,
			linked_inventory_item_id: res.fk_inventory_item_id
		}));
		setUnlinkedItems((prev: any) => ({
			unlinkedItems: prev.unlinkedItems.filter((i: any) => i !== res.fk_inventory_item_id),
			unlinkedSiteoneItems: prev.unlinkedSiteoneItems.filter((i: any) => i !== res.fk_siteone_inventory_item_id)
		}));
	};


	const clearLinkState = (inventoryId: any, siteoneId: any) => {
		// update the item in the filteredItems list to have the linked_inventory_item_id set to null
		const updatedItem = filteredItems.find((i: TInventory) => parseInt(i.item_id) === parseInt(inventoryId));
		if (updatedItem) {
			updatedItem.linked_inventory_item_id = null;
		}

		// null the previously linked inventory item id
		const previouslyLinkedItem = filteredSiteoneItems.find((i: TInventory) => parseInt(i.linked_inventory_item_id as any) === parseInt(inventoryId));
		if (previouslyLinkedItem) {
			previouslyLinkedItem.linked_inventory_item_id = null;
		}


		// update the item in the filteredSiteoneItems list to have the linked_inventory_item_id set to null
		const updatedSiteoneItem = filteredSiteoneItems.find((i: TInventory) => parseInt(i.id as any) === parseInt(siteoneId));
		if (updatedSiteoneItem) {
			updatedSiteoneItem.linked_inventory_item_id = null;
		}




		setLinkedItems((prev: any) => ({
			linkedItems: prev.linkedItems.filter((i: any) => i !== inventoryId),
			linkedSiteoneItems: prev.linkedSiteoneItems.filter((i: any) => i !== siteoneId)
		}));
		setSelectedItem((prev: any) => ({
			...prev,
			linked_inventory_item_id: null
		}));
		setSelectedSiteoneItem((prev: any) => ({
			...prev,
			linked_inventory_item_id: null
		}));
		setUnlinkedItems((prev: any) => ({
			unlinkedItems: [...prev.unlinkedItems, inventoryId],
			unlinkedSiteoneItems: [...prev.unlinkedSiteoneItems, siteoneId]
		}));
	};




	const handleLinkItemSave = async () => {
		await axios.post(
			`${process.env.REACT_APP_SERVER_URL}/api/v1/company/vendors/addLinkedItem`,
			{
				vendorId: vendorId,
				itemId: selectedItem?.item_id,
				price: price,
				leadTime: normalizeLeadTimeStored(leadTime) || null,
				notes: clampVendorItemNotes(notes) || null,
			}).then(() => {
				setPrice("");
				setLeadTime("");
				setNotes("");
				setSelectedItem(null);
				queryClient.invalidateQueries(["vendors_items", vendorId]);
				categoryItemsQuery.refetch();
				linkCategoryItemsQuery.refetch();
				if (siteoneLocalItemSearch?.trim() || siteoneLocalItemNumberSearch?.trim()) {
					allItemsBySearch.refetch();
				}
				if (linkItemSearch?.trim()) {
					linkItemsBySearch.refetch();
				}
				showSnackbar(trAction("Item Linked Successfully", "Item Added Successfully"));
			}).catch(() => {
				showSnackbar(trAction("Error Linking Item", "Error Adding Item"));
			}
			);
	}

	const handleSiteOneLinkItemSave = async () => {
		await axios.post(
			`${process.env.REACT_APP_SERVER_URL}/api/v1/company/vendors/addLinkedSiteOneItem`,
			{
				vendorId: vendorId,
				siteOneItemId: selectedSiteoneItem?.id,
				price: selectedSiteoneItem?.your_price,
			}).then(async () => {
				// Immediately update the selected item to reflect it's been saved
				if (selectedSiteoneItem) {
					setSelectedSiteoneItem({
						...selectedSiteoneItem,
						is_in_vendor_item_details: true
					});
				}
				
				// Invalidate and refetch all SiteOne item queries to ensure fresh data from backend
				await queryClient.invalidateQueries(["siteone_vendor_link_items_all"]);
				await queryClient.invalidateQueries(["siteone_vendor_link_items_category"]);
				await queryClient.invalidateQueries(["vendor_link_items_search"]);
				
				// Refetch the currently active queries
				await Promise.all([
					allItemsBySearch.refetch(),
					allSiteoneItemsQuery.refetch(),
					siteoneCategoryItemsQuery.refetch()
				]);

				queryClient.invalidateQueries(["vendors_items", vendorId]);
				
				showSnackbar("SiteOne Item Added Successfully");
			}).catch(() => {
				showSnackbar("SiteOne Item Add Error");
			}
			);
	}



	const canLink =
		!matchLoading &&
		selectedItem &&
		selectedSiteoneItem &&
		!selectedItem.linked_inventory_item_id &&
		// parseInt(selectedItem.item_id) !== selectedSiteoneItem.linked_inventory_item_id &&
		selectedSiteoneItem.linked_inventory_item_id === null;

	const canChangeLink =
		selectedItem?.linked_inventory_item_id &&
		!selectedSiteoneItem?.linked_inventory_item_id &&
		parseInt(selectedItem.item_id) !== selectedSiteoneItem?.linked_inventory_item_id;

	const canUnlink =
		selectedItem?.linked_inventory_item_id &&
		selectedSiteoneItem?.linked_inventory_item_id &&
		parseInt(selectedItem.item_id) === selectedSiteoneItem.linked_inventory_item_id;



	if (categoryQuery?.isLoading) return <Spinner />;

	if (categoryQuery?.isError) return <div>Error</div>;

	if (isHeritageVendor) {
		return (
			<div className={styles.container}>
				{/* Local Items */}
				<div className={styles.pickersContainer}>
					<h3 className="text-sm font-semibold mb-2">Your Items</h3>
					<SearchInput
						search
						placeholder="Search your items..."
						value={localSearch}
						onChange={(e) => setLocalSearch((e.target as HTMLInputElement).value)}
					/>

					{/* 🧷 Floating indicator ABOVE scrollable list */}
					{selectedItem && (
						<div className="sticky top-0 left-0 right-0 border-b bg-blue-50 text-blue-800 text-sm px-3 py-2 flex items-center justify-between z-10">
							<div className="truncate pr-2">
								Working on:{" "}
								<span className="font-medium">
									{userItems.find((i: any) => i.item_id === selectedItem)?.item_name || "—"}
								</span>
							</div>
							<div className="flex items-center gap-2">
								<button
									className="text-xs text-blue-600 hover:underline"
									onClick={handleUnlink}
								>
									Unlink
								</button>

								<button
									className="text-xs text-blue-600 hover:underline ml-4"
									onClick={() => {
										setSelectedItem(null);
										setSearch("");
									}}
								>
									Clear
								</button>
							</div>
						</div>
					)}

					{/* Scrollable list */}
					<div className="mt-2 flex-1 overflow-y-auto p-1">
						{userItems.map((item: any) => (
							<div
								key={item.item_id}
								className={`border rounded p-2 cursor-pointer text-sm relative ${selectedItem === item.item_id
									? "bg-blue-100 border-blue-600 ring-2 ring-blue-500"
									: "hover:bg-gray-50"
									}`}
								onClick={() => {
									setSelectedItem(item.item_id);
									const query = item.vendor_part_number || item.model || item.sku || item.item_name;
									setSearch(query);
								}}
								title={`Model: ${item.model || "N/A"}\nVendor Part #: ${item.vendor_part_number || "N/A"}\nSKU: ${item.sku || "N/A"}\nDesc: ${item.long_description?.slice(0, 100) || "N/A"}`}
							>
								<div className="font-semibold">{item.item_name}</div>
								<div className="text-xs text-gray-500">
									{item.model || "—"}{" "}
									<span className="text-[10px] uppercase tracking-wide text-gray-400 ml-1">Model</span>
								</div>
								<div className="text-xs text-gray-500">
									{item.sku || "—"}{" "}
									<span className="text-[10px] uppercase tracking-wide text-gray-400 ml-1">SKU</span>
								</div>
								{item.vendor_part_number && (
									<span className="absolute top-2 right-2 text-green-500">🔗</span>
								)}
							</div>
						))}
					</div>
				</div>


				{/* Heritage Items */}
				<div className={styles.pickersContainer}>
					<h3 className="text-sm font-semibold mb-2">Heritage Items</h3>
					<SearchInput
						search
						placeholder="Search heritage parts..."
						value={search}
						onChange={(e) => setSearch((e.target as HTMLInputElement).value)}
					/>
					<div className="mt-2 flex-1 overflow-y-auto pr-1">
						{heritageResults.map((item) => {
							const isLinked = userItems.some(
								(ui) => ui.vendor_part_number === item.heritage_part_number
							);
							return (
								<div
									key={item.heritage_part_number}
									className={`group border rounded p-2 cursor-pointer text-sm relative ${isLinked ? "bg-green-100 border-green-600" : "hover:bg-gray-50"}`}
									onClick={() => {
										handleLink(item.heritage_part_number);
										if (!localSearch.trim()) setLocalSearch(item.heritage_part_number);
									}}
								>
									<div className="font-semibold">{item.heritage_part_number}</div>
									<div className="text-xs text-gray-600">
										{item.description || "—"}{" "}
										<span className="text-[10px] uppercase tracking-wide text-gray-400 ml-1">Description</span>
									</div>

									{isLinked && (
										<span className="absolute top-2 right-2 text-green-600">🔗</span>
									)}

									<Button
										className="absolute top-2 right-2 hidden group-hover:flex text-xs text-blue-600 underline bg-white z-10"
										onClick={async (e: any) => {
											e.stopPropagation();
											try {
												await axios.post(`${process.env.REACT_APP_SERVER_URL}/api/v1/company/heritage/create-inventory-item`, {
													vendorId,
													heritagePart: item,
												});

												toast.success("Item created and linked!");

												const endpoint = `${process.env.REACT_APP_SERVER_URL}/api/v1/company/heritage/local-items/search`;
												const params = localSearch.trim().length >= 2 ? { q: localSearch } : {};
												const updated = await axios.get(endpoint, { params });
												setUserItems(updated.data.items || []);
											} catch {
												toast.error("Failed to create inventory item.");
											}
										}}
									>
										Import
									</Button>
								</div>
							);
						})}
					</div>
				</div>

			</div >
		);
	}


	if (isSiteoneVendor) {
		return (
			<div className={styles.container}>
				<div className={styles.pickersContainer}>

					<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
						<h2>Local Items</h2>
						{/* button that toggles between item and item number search */}
						<Button
							style={{ marginLeft: "auto", marginRight: "5px" }}
							onClick={() => {
								setLocalItemNumberSearch(!localItemNumberSearch)
								setSiteoneLocalItemNumberSearch("")
								setSiteoneLocalItemSearch("")
							}}
							disabled={!siteoneCategoryQuery.data || siteoneCategoryQuery.data.length === 0}
						>
							{localItemNumberSearch ? tr("Search by Item Name") : tr("Search by Item Number")}
						</Button>

						{
							localItemNumberSearch ? (
								<SearchInput
									search
									placeholder={tr("Search by Number")}
									value={siteoneLocalItemNumberSearch}
									onChange={(e) => setSiteoneLocalItemNumberSearch((e.target as HTMLInputElement).value)}
								/>
							) : (
								<SearchInput
									search
									placeholder={tr("Search by Name")}
									value={siteoneLocalItemSearch}
									onChange={(e) => setSiteoneLocalItemSearch((e.target as HTMLInputElement).value)}
								/>
							)
						}
					</div>

					<div className={styles.pickers}>
						{/* Category column */}
						<div className={styles.categoryList}>
							{/* if categoryQuery category_id is not in the itemsQuery, do not show it */}
							{(filteredCategories).map((category: any) => (
								<div
									key={category.category_id}
									className={
										selectedCategory?.category_id === category.category_id
											? styles.selectedCategoryContainer
											: styles.categoryContainer
									}
									onClick={() => handleChangeCategory(category)}
								>
									{/* if every item in this category is linked, show a linked icon */}
									{filteredItems?.length > 0 && filteredItems.every((item: TInventory) => item.linked_inventory_item_id && selectedCategory?.category_id === item.category_id) ? (
										<span className={styles.linkedIcon}>🔗</span>
									) : null}

									{category.category_name}
								</div>
							))}

						</div>

						{/* Filtered item list */}
						<div className={styles.itemList}>
							{
								// Show spinner when loading
								(filteredItems?.length === 0 && selectedCategory && (allItemsBySearch.isLoading || categoryItemsQuery.isLoading)) ? (
									<Spinner />
								) : 
								// Show no items found when search is active but no results
								(filteredItems?.length === 0 && selectedCategory && (siteoneLocalItemSearch?.trim() || siteoneLocalItemNumberSearch?.trim()) && !allItemsBySearch.isLoading && !categoryItemsQuery.isLoading) ? (
									<div className="text-center text-lg mt-2 font-bold">
										{tr("No items found")}
									</div>
								) : 
								// Show no category selected message
								!selectedCategory ? (
									<div className={styles.noCategorySelected}>
										{tr("Select a Category to view items")}
									</div>
								) : null
							}
							{filteredItems?.map((item: TInventory) => (
								<div
									key={item.item_id}
									className={
										selectedItem?.item_id === item.item_id
											? styles.selectedItemContainer
											: styles.itemContainer
									}
									onClick={() => handleChangeItem(item)}
								>
									{item.item_name}
									<div style={{ display: "block", fontSize: "12px", color: "#666", marginLeft: "auto", marginRight: "0", width: "fit-content" }}>
										{item.model}
									</div>
									{
										item.linked_inventory_item_id
											? (
												<span className={styles.linkedIcon}>🔗</span>
											) : null
									}
								</div>
							))}
						</div>
					</div>

				</div>



				<div style={{ width: "33%", margin: "20px" }}>
					{/* find best match between siteoneSelectedCategory and selectedCategory */}

					{/* <Button
						style={{ marginTop: "10px" }}
						onClick={async () => {
							if (!selectedItem || !siteoneCategoryQuery.data) return;

							let bestMatches: { item: TInventory; score: number }[] = [];
							let bestCategory: TInventoryCategory | null = null;

							// Group all SiteOne items by category
							const itemsGroupedByCategory = new Map<number, TInventory[]>();

							if (allSiteoneItemsQuery.data?.length > 0) {
								allSiteoneItemsQuery.data.forEach((item: TInventory) => {
									if (!itemsGroupedByCategory.has(item.line as any)) {
										itemsGroupedByCategory.set(item.line as any, []);
									}
									itemsGroupedByCategory.get(item.line as any)!.push(item);
								});
							} else {
								// Fetch items for all categories manually
								const results = await Promise.all(
									siteoneCategoryQuery.data.map(async (category: TInventoryCategory) => {
										const items = await getSiteoneInventoryItems(category.category_id);
										return { category, items };
									})
								);

								results.forEach(({ category, items }) => {
									itemsGroupedByCategory.set(category.category_id, items);
								});
							}

							// Match scoring per category
							const allResults = Array.from(itemsGroupedByCategory.entries()).map(([catId, items]) => {
								const matches = findTopMatches(selectedItem, items);
								const category = siteoneCategoryQuery.data.find((c: any) => c.category_id === catId) || null;
								return { category, matches };
							});

							const sorted = allResults
								.filter((entry) => entry.matches.length > 0)
								.sort((a, b) => b.matches[0].score - a.matches[0].score);

							if (sorted.length > 0) {
								bestMatches = sorted[0].matches;
								bestCategory = sorted[0].category;
							}

							if (bestMatches.length > 0 && bestCategory) {
								setRecommendedSiteoneItems(bestMatches);
								setRecommendedIndex(0);
								setRecommendedSiteoneCategory(bestCategory);
								setSelectedSiteoneItem(bestMatches[0] as any);
								setSiteoneSelectedCategory(bestCategory);
								showSnackbar(`Found ${bestMatches.length} matching items`);
							} else {
								showSnackbar("No suitable SiteOne matches found.");
							}
						}}
						disabled={!selectedItem || !siteoneCategoryQuery.data}
					>
						Find Closest SiteOne Match
					</Button> */}

					<div style={{ display: "flex", gap: "10px" }}>

						{
							canLink ? (
								<Button
									onClick={handleLinkSiteoneItemToInventoryItem}
									primary
								// disabled={isLinkDisabled}
								>
									Link SiteOne Item
								</Button>
							) : canChangeLink ? (
								<Button
									onClick={handleChangeLinkSiteoneItemToInventoryItem}
									primary
								>
									Change Linked Item
								</Button>
							) : canUnlink ? (
								<Button
									onClick={handleUnlinkSiteoneItemToInventoryItem}
									primary
								>
									Unlink SiteOne Item
								</Button>
							) : (
								<Button disabled>Link SiteOne Item</Button>
							)
						}


						{/* if item is linked to siteone item, show linked label */}
						{isSearchingLinkedSiteoneItem ? (
							<div style={{ color: "blue", marginTop: "10px", display: "flex", alignItems: "center", gap: "8px" }}>
								<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
								Searching for linked SiteOne item...
							</div>
						) : selectedSiteoneItem && selectedSiteoneItem.linked_inventory_item_id === selectedItem?.item_id || selectedItem?.linked_inventory_item_id ? (
							<div style={{ color: "green", marginTop: "10px" }}>
								Already Linked!
							</div>
						) : null}

					</div>

					{recommendedSiteoneItems.length > 1 && (
						<div style={{ marginTop: "10px", display: "flex", gap: "10px" }}>
							<Button
								disabled={recommendedIndex <= 0}
								onClick={() => {
									const newIndex = recommendedIndex - 1;
									setRecommendedIndex(newIndex);
									setSelectedSiteoneItem(recommendedSiteoneItems[newIndex] as any);
								}}
							>
								Back
							</Button>
							<Button
								disabled={recommendedIndex >= recommendedSiteoneItems.length - 1}
								onClick={() => {
									const newIndex = recommendedIndex + 1;
									setRecommendedIndex(newIndex);
									setSelectedSiteoneItem(recommendedSiteoneItems[newIndex] as any);
								}}
							>
								Next
							</Button>

						</div>
					)}

					<div
						style={{
							display: "flex",
							flexDirection: "row",
							gap: "10px",
							marginTop: "30px",
							alignItems: "flex-start",
						}}
					>
						{/* Local Item Column */}
						<div style={{ display: "flex", flexDirection: "column", gap: "12px", flex: 1 }}>
							<label style={{ fontWeight: 600 }}>Local Category</label>
							<input
								type="text"
								value={selectedCategory?.category_name || ""}
								readOnly
								style={{
									padding: "8px",
									border: "1px solid #ccc",
									borderRadius: "4px",
									background: "#f9f9f9",
								}}
							/>

							<label style={{ fontWeight: 600 }}>Local Item</label>
							<input
								type="text"
								value={selectedItem?.item_name || ""}
								readOnly
								style={{
									padding: "8px",
									border: "1px solid #ccc",
									borderRadius: "4px",
									background: "#f9f9f9",
								}}
							/>
							
							<label style={{ fontWeight: 600 }}>Local Item Number</label>
							<input
								type="text"
								value={selectedItem?.model || ""}
								readOnly
								style={{
									padding: "8px",
									border: "1px solid #ccc",
									borderRadius: "4px",
									background: "#f9f9f9",
								}}
							/>
						</div>

						{/* SiteOne Item Column */}
						<div style={{ display: "flex", flexDirection: "column", gap: "12px", flex: 1 }}>
							<label style={{ fontWeight: 600 }}>SiteOne Category</label>
							<input
								type="text"
								value={siteoneSelectedCategory?.category_name || ""}
								readOnly
								style={{
									padding: "8px",
									border: "1px solid #ccc",
									borderRadius: "4px",
									background: "#f0f0f0",
								}}
							/>

							<label style={{ fontWeight: 600 }}>SiteOne Item</label>
							<input
								type="text"
								value={selectedSiteoneItem?.description || ""}
								readOnly
								style={{
									padding: "8px",
									border: "1px solid #ccc",
									borderRadius: "4px",
									background: "#f0f0f0",
								}}
							/>

							<label style={{ fontWeight: 600 }}>SiteOne Item Number</label>
							<input
								type="text"
								value={selectedSiteoneItem?.item_number || ""}
								readOnly
								style={{
									padding: "8px",
									border: "1px solid #ccc",
									borderRadius: "4px",
									background: "#f0f0f0",
								}}
							/>
						</div>
					</div>


					{selectedSiteoneItem ? (
						<>
							<div className={styles.itemDetailsContainer}>
								<>
									<img
										alt={selectedSiteoneItem.item_name}
										src={selectedItem?.imageUrl ? selectedItem.imageUrl : NoProductImage}
										className={styles.image}
									/>

									<div className={styles.detailGroup}>
										<div className={styles.itemName}>{selectedSiteoneItem.item_name}</div>
										<div className={styles.description}>{selectedSiteoneItem.description}</div>
									</div>

								</>
							</div>
							<div className={styles.formContainer}>
								{/* <Field
								advancedNumericalInputHandling
								value={selectedSiteoneItem?.your_price || ""}
								// setValue={setPrice}
								placeholder={tr("Price")}
								style={{ marginBottom: "10px" }}
								// disable this
								readOnly
							/> */}
								<label style={{ fontWeight: 600, marginBottom: "10px" }}>
									Price
								</label>
								<Input
									style={{ marginBottom: "10px", backgroundColor: "#f0f0f0" }}
									placeholder={tr("Price")}
									value={selectedSiteoneItem?.your_price || ""}
									readOnly
								/>
								{/* <Input
								style={{ marginBottom: "10px" }}
								placeholder={tr("Lead Time")}
								value={leadTime}
								onChange={(e: any) => setLeadTime(e.target.value)}
							/>
							<Input
								style={{ marginBottom: "10px" }}
								placeholder={tr("Notes")}
								value={notes}
								onChange={(e: any) => setNotes(e.target.value)}
							/> */}
								<div style={{ marginTop: "20px" }}>
									{selectedSiteoneItem?.is_in_vendor_item_details ? (
										<div style={{
											backgroundColor: "#f0fdf4",
											border: "1px solid #86efac",
											borderRadius: "8px",
											padding: "12px 16px",
											display: "flex",
											alignItems: "center",
											gap: "10px",
											marginBottom: "12px"
										}}>
											<svg 
												width="20" 
												height="20" 
												viewBox="0 0 20 20" 
												fill="none" 
												xmlns="http://www.w3.org/2000/svg"
												style={{ flexShrink: 0 }}
											>
												<path 
													d="M10 0C4.48 0 0 4.48 0 10C0 15.52 4.48 20 10 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 10 0ZM8 15L3 10L4.41 8.59L8 12.17L15.59 4.58L17 6L8 15Z" 
													fill="#16a34a"
												/>
											</svg>
											<div style={{ flex: 1 }}>
												<div style={{ 
													color: "#15803d", 
													fontWeight: 600,
													fontSize: "14px",
													marginBottom: "2px"
												}}>
													Already Saved
												</div>
												<div style={{ 
													color: "#166534", 
													fontSize: "13px"
												}}>
													This item is already saved to vendor items
												</div>
											</div>
										</div>
									) : null}
									
									<Button
										className={styles.saveButton}
										onClick={handleSiteOneLinkItemSave}
										primary
										disabled={!selectedSiteoneItem || selectedSiteoneItem?.is_in_vendor_item_details}
										style={{ width: "100%" }}
									>
										{selectedSiteoneItem?.is_in_vendor_item_details 
											? "Already Saved to Vendor Items" 
											: tr("Save")
										}
									</Button>
								</div>
							</div>

						</>
					) : null}


				</div>

				<div className={styles.pickersContainer}>

					<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
						<h2>SiteOne Items</h2>
						{/* button that toggles between item and item number search */}
						<Button
							style={{ marginLeft: "auto", marginRight: "5px" }}
							onClick={() => {
								const newMode = !siteoneItemNumberSearch;
								setSiteoneItemNumberSearch(newMode);
								
								// If there's a selected LOCAL item
								if (selectedItem) {
									// If item is already linked, clear search (let useEffect find linked item)
									if (selectedItem.linked_inventory_item_id) {
										setSiteoneSiteoneItemNumberSearch("");
										setSiteoneSiteoneItemSearch("");
									} else {
										// Only populate search if item is NOT linked
										if (newMode) {
											// Switching to number search - use local item's model
											setSiteoneSiteoneItemNumberSearch(selectedItem.model || "");
											setSiteoneSiteoneItemSearch("");
										} else {
											// Switching to name search - use local item's name
											setSiteoneSiteoneItemSearch(selectedItem.item_name || "");
											setSiteoneSiteoneItemNumberSearch("");
										}
									}
								} else {
									setSiteoneSiteoneItemNumberSearch("");
									setSiteoneSiteoneItemSearch("");
								}
							}}
							disabled={!siteoneCategoryQuery.data || siteoneCategoryQuery.data.length === 0}
						>
							{siteoneItemNumberSearch ? tr("Search by Item Name") : tr("Search by Item Number")}
						</Button>

						{
							siteoneItemNumberSearch ? (
								<SearchInput
									search
									placeholder={tr("Search by Number")}
									value={siteoneSiteoneItemNumberSearch}
									onChange={(e) => setSiteoneSiteoneItemNumberSearch((e.target as HTMLInputElement).value)}
								/>
							) : (
								<SearchInput
									search
									placeholder={tr("Search by Name")}
									value={siteoneSiteoneItemSearch}
									onChange={(e) => setSiteoneSiteoneItemSearch((e.target as HTMLInputElement).value)}
								/>
							)
						}
					</div>

					<div className={styles.pickers}>
						<div className={styles.categoryList}>
							{filteredSiteoneCategories?.map((category: TInventoryCategory) => {
								const isSelected = siteoneSelectedCategory?.category_id === category.category_id;
								const isRecommended = recommendedSiteoneCategory?.category_id === category.category_id;

								return (
									<div
										className={
											isSelected
												? styles.selectedCategoryContainer
												: isRecommended
													? styles.recommendedCategoryContainer
													: styles.categoryContainer
										}
										onClick={() => handleSiteoneChangeCategory(category)}
									>
										{category.category_name}
									</div>
								);
							})}
						</div>

						<div className={styles.itemList}>
							{
								// Show spinner when loading
								(filteredSiteoneItems?.length === 0 && siteoneSelectedCategory && (allSiteoneItemsQuery.isLoading || siteoneCategoryItemsQuery.isLoading)) ? (
									<Spinner />
								) : 
								// Show no items found when search is active but no results
								(filteredSiteoneItems?.length === 0 && siteoneSelectedCategory && (siteoneSiteoneItemSearch?.trim() || siteoneSiteoneItemNumberSearch?.trim()) && !allSiteoneItemsQuery.isLoading && !siteoneCategoryItemsQuery.isLoading) ? (
									<div className="text-center text-lg mt-2 font-bold">
										{tr("No items found")}
									</div>
								) : 
								// Show no category selected message
								!siteoneSelectedCategory ? (
									<div className={styles.noCategorySelected}>
										{tr("Select a Category to view items")}
									</div>
								) : null
							}

							{/* Recommended items */}
							{filteredSiteoneItems?.map((item: TInventory) => {
								const recommended = recommendedSiteoneItems?.find(r => r.item.id === item.id);
								const score = recommended?.score ?? null;
								const maxScore = recommendedSiteoneItems.length > 0 ? recommendedSiteoneItems[0].score : 1;

								const backgroundColor = score ? getGreenShade(score, maxScore) : undefined;

								return (
									<div
										ref={item.id === currentRecommendedItem?.id ? recommendedItemRef : null}
										className={
											item.id === currentRecommendedItem?.id
												? styles.recommendedItemContainer
												: item.id === selectedSiteoneItem?.id
													? styles.selectedItemContainer
													: styles.itemContainer
										}
										style={score ? { backgroundColor } : {}}
										onClick={() => handleSiteoneChangeItem(item)}
									>
										{item.description}
										<div style={{ display: "block", fontSize: "12px", color: "#666", marginLeft: "auto", marginRight: "0", width: "fit-content" }}>
											{item.item_number}
										</div>

										{/* if already linked, show linked icon */}
										{
											item.linked_inventory_item_id
												? (
													<span className={styles.linkedIcon}>🔗</span>
												) : null
										}
									</div>
								);
							})}
						</div>

					</div>
				</div>


			</div>
		)
	}

	// return  <div></div>


	return (
		<div className={styles.container}>
			<div className={styles.pickersContainer}>
				<SearchInput
					search
					placeholder={tr("Search by Name")}
					value={linkItemSearch}
					onChange={(e) => setLinkItemSearch((e.target as HTMLInputElement).value)}
					style={{ width: "100%", marginBottom: "10px" }}
				/>

				<div className={styles.pickers}>
					<div className={styles.categoryList}>
						{linkFilteredCategories?.map((category: TInventoryCategory) => (
							<div
								key={category.category_id}
								className={
								selectedCategory && category.category_id === selectedCategory.category_id
									? styles.selectedCategoryContainer
									: styles.categoryContainer
							}
							onClick={() => handleChangeCategory(category)}
						>
							{category.category_name}
						</div>
					))}
				</div>
				<div className={styles.itemList}>
						{linkFilteredItems?.length === 0 && selectedCategory && (linkItemsBySearch.isLoading || linkCategoryItemsQuery.isLoading) ? (
						<Spinner />
					) : null}
						{linkCategoryItemsQuery?.isError || linkItemsBySearch?.isError ? <div>{tr("Error")}</div> : null}
						{linkItemSearch?.trim() && linkFilteredCategories.length === 0 && !linkItemsBySearch.isLoading ? (
							<div className={styles.noCategorySelected}>
								{tr("No items found")}
							</div>
						) : null}
						{!linkItemSearch?.trim() && !selectedCategory ? (
							<div className={styles.noCategorySelected}>
								{trAction("Select a Category to view items", "Choose a Category")}
							</div>
						) : null}
						{linkItemSearch?.trim() && selectedCategory && linkFilteredItems.length === 0 && !linkItemsBySearch.isLoading ? (
							<div className={styles.noCategorySelected}>
								{tr("No items found")}
							</div>
						) : null}
						{linkFilteredItems?.map((item: TInventory) => (
						<div
							key={item.item_id}
							className={
								selectedItem && item.item_id === selectedItem.item_id
									? styles.selectedItemContainer
									: styles.itemContainer
							}
							onClick={() => handleChangeItem(item)}
						>
							{item.item_name}
						</div>
						))}
					</div>
				</div>
			</div>

			<div style={{ margin: "20px", width: "50%" }}>

				{selectedItem ? (
					<>
						<div className={styles.itemDetailsContainer}>
							<>
								<img
									alt={selectedItem.item_name}
									src={selectedItem?.imageUrl ? selectedItem.imageUrl : NoProductImage}
									className={styles.image}
								/>

								<div className={styles.detailGroup}>
									<div className={styles.itemName}>{selectedItem.item_name}</div>
									<div className={styles.description}>{selectedItem.long_description}</div>
								</div>

							</>
						</div>
						
						{isIntegratedVendor && isSearchingLinkedLocalItem && (
							<div style={{ color: "blue", marginTop: "10px", display: "flex", alignItems: "center", gap: "8px" }}>
								<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
								Searching for linked local item...
							</div>
						)}
						<div className={styles.formContainer}>
							<VendorItemPriceInput
								value={price}
								onChange={setPrice}
								placeholder={tr("Price")}
								className="border px-2 py-1 rounded text-sm w-full h-[40px] min-h-[40px] max-h-[40px] box-border mb-2"
							/>
							<label style={{ fontWeight: 600, marginBottom: "4px", display: "block" }}>
								{tr("Lead Time")}
							</label>
							<div className="mb-2">
								<VendorLeadTimeInput
									value={leadTime}
									onChange={setLeadTime}
								/>
							</div>
							<label style={{ fontWeight: 600, marginBottom: "4px", display: "block" }}>
								{tr("Notes")}
							</label>
							<VendorItemNotesInput
								value={notes}
								onChange={setNotes}
								className="border px-2 py-1 rounded text-sm w-full h-[40px] min-h-[40px] max-h-[40px] box-border mb-2"
							/>
							<div className={styles.footerRow}>
								<Button
									className={styles.saveButton}
									onClick={handleLinkItemSave}
									primary
									disabled={!price}
								>
									{tr("Save")}
								</Button>
							</div>
						</div>

					</>
				) : null}
			</div>

		</div>
	);

};

export default VendorItemLinkModalBody;
